import { useSyncExternalStore } from 'react';
import type { CanvasElement } from '@/lib/engineTypes';
import type { Conflict } from '@/lib/conflictEngine';
import { composePrompt } from '@/lib/promptComposerEngine';
import { analyzePrompt, type PromptAnalysisResult } from '@/lib/promptAnalyzer';

export type PromptSections = Record<string, unknown>;
export type PromptSettings = Record<string, unknown>;

export interface PromptState {
  basePrompt: string;
  canvasElements: CanvasElement[];
  sections: PromptSections;
  settings: PromptSettings;
  finalPrompt: string;
  conflicts: Conflict[];
  analysis: PromptAnalysisResult | null;
}

interface PromptStoreSnapshot extends PromptState {
  setBasePrompt: (basePrompt: string) => void;
  setCanvasElements: (canvasElements: CanvasElement[]) => void;
  setSections: (sections: PromptSections) => void;
  setSettings: (settings: PromptSettings) => void;
  setFinalPrompt: (finalPrompt: string) => void;
  setConflicts: (conflicts: Conflict[]) => void;
  syncFromCanvas: (payload: {
    basePrompt: string;
    canvasElements: CanvasElement[];
    sections?: PromptSections;
    settings?: PromptSettings;
    conflicts?: Conflict[];
  }) => void;
  reset: () => void;
}

const DEFAULT_PROMPT_STATE: PromptState = {
  basePrompt: '',
  canvasElements: [],
  sections: {},
  settings: {},
  finalPrompt: '',
  conflicts: [],
  analysis: null,
};

const shallowEqual = (left: object, right: object): boolean => {
  if (left === right) return true;

  const leftEntries = Object.entries(left);
  const rightEntries = Object.entries(right);

  if (leftEntries.length !== rightEntries.length) {
    return false;
  }

  return leftEntries.every(([key, value]) => Object.is(value, (right as Record<string, unknown>)[key]));
};

const isStateEqual = (prev: PromptState, next: PromptState): boolean => {
  return (
    prev.basePrompt === next.basePrompt &&
    prev.finalPrompt === next.finalPrompt &&
    prev.canvasElements === next.canvasElements &&
    prev.conflicts === next.conflicts &&
    prev.analysis === next.analysis &&
    shallowEqual(prev.sections, next.sections) &&
    shallowEqual(prev.settings, next.settings)
  );
};

const hasComposerInputsChanged = (prev: PromptState, next: PromptState): boolean => {
  return (
    prev.basePrompt !== next.basePrompt ||
    prev.canvasElements !== next.canvasElements ||
    !shallowEqual(prev.sections, next.sections) ||
    !shallowEqual(prev.settings, next.settings)
  );
};

class PromptStore {
  private state: PromptState = DEFAULT_PROMPT_STATE;
  private listeners = new Set<() => void>();

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = () => this.state;

  setState = (
    updater: Partial<PromptState> | ((prev: PromptState) => PromptState),
    options: { skipCompose?: boolean } = {}
  ) => {
    const rawNextState =
      typeof updater === 'function'
        ? updater(this.state)
        : {
            ...this.state,
            ...updater,
          };

    const nextState: PromptState = { ...rawNextState };

    if (!options.skipCompose && hasComposerInputsChanged(this.state, rawNextState)) {
      nextState.finalPrompt = composePrompt(nextState);
    }

    if (this.state.finalPrompt !== nextState.finalPrompt) {
      nextState.analysis = analyzePrompt(nextState.finalPrompt);
    }

    if (isStateEqual(this.state, nextState)) {
      return;
    }

    this.state = nextState;
    this.listeners.forEach((listener) => listener());
  };

  setBasePrompt = (basePrompt: string) => this.setState({ basePrompt });
  setCanvasElements = (canvasElements: CanvasElement[]) => this.setState({ canvasElements });
  setSections = (sections: PromptSections) => this.setState({ sections });
  setSettings = (settings: PromptSettings) => this.setState({ settings });
  setFinalPrompt = (finalPrompt: string) => this.setState({ finalPrompt }, { skipCompose: true });
  setConflicts = (conflicts: Conflict[]) => this.setState({ conflicts });

  syncFromCanvas = ({
    basePrompt,
    canvasElements,
    sections = {},
    settings = {},
    conflicts = [],
  }: {
    basePrompt: string;
    canvasElements: CanvasElement[];
    sections?: PromptSections;
    settings?: PromptSettings;
    conflicts?: Conflict[];
  }) => {
    this.setState((prev) => ({
      ...prev,
      basePrompt,
      canvasElements,
      sections,
      settings,
      conflicts,
    }));
  };

  reset = () => {
    this.setState(DEFAULT_PROMPT_STATE, { skipCompose: true });
  };
}

const promptStore = new PromptStore();

const storeActions = {
  setBasePrompt: promptStore.setBasePrompt,
  setCanvasElements: promptStore.setCanvasElements,
  setSections: promptStore.setSections,
  setSettings: promptStore.setSettings,
  setFinalPrompt: promptStore.setFinalPrompt,
  setConflicts: promptStore.setConflicts,
  syncFromCanvas: promptStore.syncFromCanvas,
  reset: promptStore.reset,
};

const getStoreSnapshot = () => promptStore.getState();

export const useGlobalPromptStore = (): PromptStoreSnapshot => {
  const state = useSyncExternalStore(promptStore.subscribe, getStoreSnapshot, getStoreSnapshot);
  return {
    ...state,
    ...storeActions,
  };
};

export const promptStoreActions = storeActions;
